import { Component } from '@theme/component';

/**
 * Accordion Mega Menu Component
 * Handles sliding panel navigation for multi-level menu system.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [backButtons] - Back button elements
 *
 * @extends {Component<Refs>}
 */
class AccordionMegaMenuComponent extends Component {
  #controller = new AbortController();
  #currentLevel = 1;
  #panelStack = [];
  #hoverTimeout = null;
  #level3HoverTimeout = null;
  #currentLevel3Panel = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;

    // Listen for arrow button clicks on LEVEL 1 only (level 2 uses hover)
    const level1Arrows = this.querySelectorAll('.accordion-mega-menu__panel--level-1 .accordion-mega-menu__arrow[data-submenu-id]');
    level1Arrows.forEach((button) => {
      button.addEventListener('click', this.#handleSubmenuClick, { signal });
    });

    // Listen for back button clicks
    const backButtons = this.querySelectorAll('.accordion-mega-menu__back');
    backButtons.forEach((button) => {
      button.addEventListener('click', this.#handleBackClick, { signal });
    });

    // Setup Shop All hover handling
    this.#setupShopAllHover();

    // Setup level 2 hover handlers for level 3 panels
    this.#setupLevel2Hover();
  }

  /**
   * Sets up hover handling for Shop All menu item
   */
  #setupShopAllHover() {
    const triggerText = this.dataset.triggerText || 'Shop All';
    const shopAllLinks = Array.from(document.querySelectorAll('.menu-list__link-title'));
    const shopAllLinkElement = shopAllLinks.find((el) => el.textContent.trim() === triggerText);

    if (!shopAllLinkElement) return;

    const menuItem = shopAllLinkElement.closest('.menu-list__list-item');
    const container = document.getElementById('accordion-mega-menu-container');

    if (!menuItem || !container) return;

    const { signal } = this.#controller;

    menuItem.addEventListener('mouseenter', () => {
      clearTimeout(this.#hoverTimeout);
      this.#showMenu(container, menuItem);
    }, { signal });

    menuItem.addEventListener('mouseleave', () => {
      this.#hoverTimeout = setTimeout(() => {
        this.#hideMenu(container);
      }, 350);
    }, { signal });

    container.addEventListener('mouseenter', () => {
      clearTimeout(this.#hoverTimeout);
    }, { signal });

    container.addEventListener('mouseleave', () => {
      this.#hoverTimeout = setTimeout(() => {
        this.#hideMenu(container);
      }, 350);
    }, { signal });
  }

  /**
   * Sets up hover handling for level 2 items to show level 3 panels
   */
  #setupLevel2Hover() {
    const { signal } = this.#controller;

    // Find all level 2 items that have children (level 3 panels)
    const level2Items = this.querySelectorAll('.accordion-mega-menu__item--tier-2');
    
    level2Items.forEach((item) => {
      const arrow = item.querySelector('.accordion-mega-menu__arrow[data-submenu-id]');
      if (!arrow) return;

      const submenuId = arrow.dataset.submenuId;
      const level3Panel = this.querySelector(`#${submenuId}.accordion-mega-menu__panel--level-3`);
      
      if (!level3Panel) return;

      item.addEventListener('mouseenter', () => {
        this.#handleLevel2ItemHover(item, level3Panel);
      }, { signal });

      item.addEventListener('mouseleave', () => {
        this.#scheduleHideLevel3Panel();
      }, { signal });

      // Keep panel open when hovering over the level 3 panel itself
      level3Panel.addEventListener('mouseenter', () => {
        clearTimeout(this.#level3HoverTimeout);
      }, { signal });

      level3Panel.addEventListener('mouseleave', () => {
        this.#scheduleHideLevel3Panel();
      }, { signal });
    });
  }

  /**
   * Handles hover over a level 2 item
   * @param {HTMLElement} item - The level 2 item being hovered
   * @param {HTMLElement} panel - The level 3 panel to show
   */
  #handleLevel2ItemHover(item, panel) {
    clearTimeout(this.#level3HoverTimeout);

    // Check if we're on level 2 (level 2 panel is active)
    const activePanel = this.#getCurrentActivePanel();
    const isLevel2Active = activePanel?.classList.contains('accordion-mega-menu__panel--level-2');
    
    if (!isLevel2Active) return;

    // Remove hover-active class from previously hovered item
    const previouslyActive = this.querySelector('.accordion-mega-menu__item--hover-active');
    if (previouslyActive) {
      previouslyActive.classList.remove('accordion-mega-menu__item--hover-active');
    }

    // Add hover-active class to current item
    item.classList.add('accordion-mega-menu__item--hover-active');

    // Show the level 3 panel
    this.#showLevel3Panel(panel, item);
  }

  /**
   * Shows a level 3 panel positioned to the right of the menu
   * @param {HTMLElement} panel - The panel to show
   * @param {HTMLElement} sourceItem - The level 2 item that triggered the hover
   */
  #showLevel3Panel(panel, sourceItem) {
    // Hide currently showing level 3 panel if different
    if (this.#currentLevel3Panel && this.#currentLevel3Panel !== panel) {
      this.#currentLevel3Panel.classList.remove('accordion-mega-menu__panel--hover-active');
    }

    // Position and show the new panel
    this.#positionLevel3Panel(panel, sourceItem);
    panel.classList.add('accordion-mega-menu__panel--hover-active');
    
    this.#currentLevel3Panel = panel;
  }

  /**
   * Positions a level 3 panel to the right of the menu
   * @param {HTMLElement} panel - The panel to position
   * @param {HTMLElement} sourceItem - The level 2 item that triggered the hover
   */
  #positionLevel3Panel(panel, sourceItem) {
    // Use fixed positioning to escape overflow: hidden on the menu
    const menuRect = this.getBoundingClientRect();
    const itemRect = sourceItem.getBoundingClientRect();
    
    // Position to the right of the menu
    const leftOffset = 71;
    const left = menuRect.left + leftOffset;
    
    // Position relative to the hovered item
    const topOffset = -129;
    const top = itemRect.top + topOffset;

    panel.style.left = `${left}px`;
    panel.style.top = `${top}px`;
  }

  /**
   * Schedules hiding of level 3 panels after a delay
   */
  #scheduleHideLevel3Panel() {
    clearTimeout(this.#level3HoverTimeout);
    this.#level3HoverTimeout = setTimeout(() => {
      this.#hideLevel3Panels();
    }, 250);
  }

  /**
   * Hides all level 3 panels
   */
  #hideLevel3Panels() {
    if (this.#currentLevel3Panel) {
      this.#currentLevel3Panel.classList.remove('accordion-mega-menu__panel--hover-active');
      this.#currentLevel3Panel = null;
    }

    // Remove hover-active class from all level 2 items
    const hoverActiveItems = this.querySelectorAll('.accordion-mega-menu__item--hover-active');
    hoverActiveItems.forEach((item) => {
      item.classList.remove('accordion-mega-menu__item--hover-active');
    });
  }

  /**
   * Shows the accordion menu positioned under Shop All
   * @param {HTMLElement} container - The menu container
   * @param {HTMLElement} menuItem - The Shop All menu item
   */
  #showMenu(container, menuItem) {
    const rect = menuItem.getBoundingClientRect();
    container.style.position = 'fixed';
    container.style.top = `${rect.bottom}px`;
    container.style.left = `${rect.left + rect.width / 2}px`;
    container.style.transform = 'translateX(-50%)';
    container.style.zIndex = '99999';
    container.hidden = false;
  }

  /**
   * Hides the accordion menu
   * @param {HTMLElement} container - The menu container
   */
  #hideMenu(container) {
    container.hidden = true;
    this.reset();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles submenu button click to slide in next level.
   * @param {MouseEvent} event - The click event.
   */
  #handleSubmenuClick = (event) => {
    const button = /** @type {HTMLElement} */ (event.currentTarget);
    const submenuId = button.dataset.submenuId;
    const submenuTitle = button.dataset.submenuTitle;

    if (!submenuId) return;

    this.#showPanel(submenuId, submenuTitle);
  };

  /**
   * Shows the specified panel level.
   * @param {number} level - The level to show (2 or 3)
   * @param {string} title - The title for the panel
   * @param {HTMLElement} sourceButton - The button that triggered the navigation
   */
  /**
   * Shows a specific panel by ID.
   * @param {string} panelId - The ID of the panel to show
   * @param {string} [title] - Optional title for the back button
   */
  #showPanel(panelId, title) {
    const currentPanel = this.#getCurrentActivePanel();
    const nextPanel = this.querySelector(`#${panelId}`);

    if (!currentPanel || !nextPanel) return;

    // Slide current panel out to the left
    currentPanel.classList.remove('accordion-mega-menu__panel--active');
    currentPanel.classList.add('accordion-mega-menu__panel--slide-left');

    // Slide next panel in from the right
    nextPanel.classList.add('accordion-mega-menu__panel--active');

    // Track panel for back navigation
    this.#panelStack.push(currentPanel.id || 'level-1');
  }

  /**
   * Gets the currently active panel.
   * @returns {HTMLElement|null}
   */
  #getCurrentActivePanel() {
    return this.querySelector('.accordion-mega-menu__panel--active');
  }

  /**
   * Handles back button click to return to previous level.
   */
  #handleBackClick = () => {
    if (this.#panelStack.length === 0) return;

    const currentPanel = this.#getCurrentActivePanel();
    const previousPanelId = this.#panelStack.pop();

    // Find previous panel by ID or fall back to level-1
    const previousPanel = previousPanelId === 'level-1'
      ? this.querySelector('.accordion-mega-menu__panel--level-1')
      : this.querySelector(`#${previousPanelId}`);

    if (!currentPanel || !previousPanel) return;

    // Hide level 3 panels when navigating back
    this.#hideLevel3Panels();

    // Slide current panel back to the right
    currentPanel.classList.remove('accordion-mega-menu__panel--active');

    // Slide previous panel back from the left
    previousPanel.classList.remove('accordion-mega-menu__panel--slide-left');
    previousPanel.classList.add('accordion-mega-menu__panel--active');
  };

  /**
   * Resets the menu to level 1.
   */
  reset() {
    // Hide level 3 panels
    this.#hideLevel3Panels();

    // Reset all panels to initial state
    const allPanels = this.querySelectorAll('.accordion-mega-menu__panel');
    allPanels.forEach((panel) => {
      panel.classList.remove('accordion-mega-menu__panel--active');
      panel.classList.remove('accordion-mega-menu__panel--slide-left');
      panel.classList.remove('accordion-mega-menu__panel--hover-active');
    });

    const level1Panel = this.querySelector('.accordion-mega-menu__panel--level-1');
    if (level1Panel) {
      level1Panel.classList.add('accordion-mega-menu__panel--active');
    }

    this.#currentLevel = 1;
    this.#panelStack = [];
  }
}

if (!customElements.get('accordion-mega-menu-component')) {
  customElements.define('accordion-mega-menu-component', AccordionMegaMenuComponent);
}

// Setup hover handlers independently from the component lifecycle
document.addEventListener('DOMContentLoaded', () => {
  const container = document.getElementById('accordion-mega-menu-container');
  if (!container) return;

  // Get trigger text from the component data attribute
  const component = container.querySelector('accordion-mega-menu-component');
  const triggerText = component?.dataset?.triggerText || 'Shop All';

  const shopAllLinks = Array.from(document.querySelectorAll('.menu-list__link-title'));
  const shopAllLinkElement = shopAllLinks.find((el) => el.textContent.trim() === triggerText);

  if (!shopAllLinkElement) return;

  const menuItem = shopAllLinkElement.closest('.menu-list__list-item');
  if (!menuItem) return;

  let hoverTimeout = null;

  const showMenu = () => {
    clearTimeout(hoverTimeout);
    const rect = menuItem.getBoundingClientRect();
    container.style.position = 'fixed';
    container.style.top = `${rect.bottom}px`;
    container.style.left = `${rect.left + rect.width / 2}px`;
    container.style.transform = 'translateX(-50%)';
    container.style.zIndex = '99999';
    container.hidden = false;
  };

  const hideMenu = () => {
    hoverTimeout = setTimeout(() => {
      container.hidden = true;
      const component = container.querySelector('accordion-mega-menu-component');
      if (component?.reset) component.reset();
    }, 350);
  };

  menuItem.addEventListener('mouseenter', showMenu);
  menuItem.addEventListener('mouseleave', hideMenu);
  container.addEventListener('mouseenter', () => clearTimeout(hoverTimeout));
  container.addEventListener('mouseleave', hideMenu);
});
